import { Col, Row } from 'reactstrap';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BsArrowLeftShort } from 'react-icons/bs';
import { useDispatch, useSelector } from 'react-redux';
import NavigationFormFields from './components/NavigationFormFields';
import VtSelectionModal from './components/VtSelectionModal';
import { Formik } from 'formik';
import { useNavigate } from 'react-router-dom';
import {
  buildNavGraph,
  dijkstra,
  findPinNodeId,
  findSortedVtPinIds,
  haversineM,
} from '../../../../../components/map/components/Map/Navigation/navigationUtils';
import {
  setShortestPath,
  clearShortestPath,
} from '../../../../../store/slices/navigationSlice';
import { setCurrentFloor } from '../../../../../store/slices/projectItemSlice';

function Navigation() {

    const dispatch    = useDispatch();
    const navigate    = useNavigate();

    const allPins      = useSelector((s) => s.api.allPins);
    const paths        = useSelector((s) => s.navigation.paths);
    const shortestPath = useSelector((s) => s.navigation.shortestPath);
    const currentFloor = useSelector((s) => s.api.currentFloor);
    const floorList    = useSelector((s) => s.api.floorList);
    const map          = useSelector((s) => s.map.mapContainer);
    const floorLoading = useSelector((state) => state.api.floorLoading);

    const [mapDivSize, setMapDivSize]           = useState(window.innerHeight);
    const [options, setOptions]                 = useState([]);
    const [pathError, setPathError]             = useState(null);
    const [segmentIndex, setSegmentIndex]       = useState(null);
    const [showVtModal, setShowVtModal]         = useState(false);
    const [vtTypes, setVtTypes]                 = useState([]);
    const [isWheelchair, setIsWheelchair]       = useState(false);
    const [currentFormDetails, setCurrentFormDetails] = useState({
        from: '',
        to:   '',
    });

    useEffect(() => {
        const { vertical, vertical_transport, ...restPins } = allPins || {};

        const flat        = Object.values(restPins).flat();
        const regularPins = flat.filter((pin) => !!pin?.positions);

        const verticalPins = (vertical || []).flatMap((vt) =>
            (vt.connections || []).map((conn) => {
                const floor = floorList?.find((f) => String(f.enc_id) === String(conn.fp_id));
                return {
                    enc_id:     vt.enc_id,
                    title:      vt.vt_name || 'Vertical Transport',
                    positions:  conn.positions,
                    fp_id:      conn.fp_id,
                    floor_plan: floor?.fp_name || floor?.floor_name || floor?.name || `Floor ${conn.fp_id}`,
                    category:   'vertical',
                };
            }).filter((p) => !!p.positions)
        );

        setOptions([...regularPins, ...verticalPins]);
    }, [allPins, floorList]);

    useEffect(() => {
        const handleResize = () => setMapDivSize(window.innerHeight);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const goBack = () => {
        navigate(-1);
    };

    const pendingFitRef = useRef(null);

    const fitSegmentOnMap = useCallback((positions) => {
        if (!map || !positions?.length) return;
        if (positions.length === 1) {
            map.flyTo({ center: positions[0], zoom: 20, speed: 1.5, essential: true });
            return;
        }
        const lngs   = positions.map((p) => p[0]);
        const lats   = positions.map((p) => p[1]);
        const bounds = [
            [Math.min(...lngs), Math.min(...lats)],
            [Math.max(...lngs), Math.max(...lats)],
        ];
        map.fitBounds(bounds, { padding: 80, maxZoom: 22, speed: 1.5, essential: true });
    }, [map]);

    const buildVtMovementMap = useCallback(() => {
        const map = {};
        for (const vt of (allPins?.vertical || [])) {
            map[String(vt.enc_id)] = vt.movement_direction || 'bidirectional';
        }
        return map;
    }, [allPins]);

    const buildFloorOrderMap = useCallback(() => {
        const map = {};
        // (floorList || []).forEach((floor, idx) => {
        //     // map[String(floor.enc_id)] = floor.display_index ?? idx;
        //     map[String(floor.enc_id)] = idx;
        // });
        (floorList || []).forEach((floor, idx, arr) => { 
            map[String(floor.enc_id)] = arr.length - 1 - idx;
        });
 
        return map;
    }, [floorList]);

    const isGoingUp = useCallback((fromFloorId, toFloorId) => {
        const floorOrderMap = buildFloorOrderMap();
        const fromOrder = floorOrderMap[String(fromFloorId)] ?? 0;
        const toOrder   = floorOrderMap[String(toFloorId)]   ?? 0;
        return toOrder > fromOrder;
    }, [buildFloorOrderMap]);

    const runPathAndDispatch = useCallback((fromPinId, toPinId, allowedVtPinIds = null) => {
        const startId = findPinNodeId(paths, fromPinId);
        const endId   = findPinNodeId(paths, toPinId);

        if (!startId) {
            setPathError('The "From" pin is not connected to any navigation path. Draw a connection first.');
            return false;
        }
        if (!endId) {
            setPathError('The "To" pin is not connected to any navigation path. Draw a connection first.');
            return false;
        }

        const vtMovementMap = buildVtMovementMap();
        const floorOrderMap = buildFloorOrderMap();

        const { nodes, adj } = buildNavGraph(paths, true, allowedVtPinIds, vtMovementMap, floorOrderMap); 


        const result = dijkstra(nodes, adj, startId, endId);
         
        if (!result) {
            setPathError(
                allowedVtPinIds
                    ? 'No path found via the selected transport type. Try a different option.'
                    : 'No connected path found between the selected locations.'
            );
            return false;
        }

        const nodeFloorMap   = {};   
        const nodeIdToPinId  = {};   
        for (const path of paths) {
            for (const pt of path.points) {
                nodeFloorMap[pt.id]  = path.floorId;
                if (pt.pinId) nodeIdToPinId[pt.id] = String(pt.pinId);
            }
        }
 
        const vtEncIds = new Set((allPins?.vertical || []).map((vt) => String(vt.enc_id)));

        const positions = result.nodeIds.map((id) => nodes[id]).filter(Boolean);

        let realDistanceM = 0;
        for (let i = 1; i < positions.length; i++) {
            realDistanceM += haversineM(positions[i - 1], positions[i]);
        }
 
        const rawSegments = [];
        for (const nodeId of result.nodeIds) {
            const pos     = nodes[nodeId];
            const floorId = nodeFloorMap[nodeId];
            if (!pos) continue;
            const lastSeg = rawSegments[rawSegments.length - 1];
            if (!lastSeg || lastSeg.floorId !== floorId) {
                const floor     = floorList?.find((f) => String(f.enc_id) === String(floorId));
                const floorName = floor?.fp_name || floor?.floor_plan || `Floor ${floorId}`;
                rawSegments.push({ floorId, floorName, positions: [pos], _nodeIds: [nodeId] });
            } else {
                lastSeg.positions.push(pos);
                lastSeg._nodeIds.push(nodeId);
            }
        }
 
        const segments = rawSegments
            .filter((seg, i) => {
                const isIntermediate = i > 0 && i < rawSegments.length - 1;
                if (!isIntermediate) return true;               
                if (seg._nodeIds.length !== 1) return true;    
                const pinId = nodeIdToPinId[seg._nodeIds[0]];
                return !(pinId && vtEncIds.has(pinId));        
            })
            .map(({ _nodeIds, ...seg }) => seg);         

        dispatch(setShortestPath({
            positions,
            distanceM:    realDistanceM,
            segments,
            isMultiFloor: segments.length > 1,
        }));

        const fromFloor = floorList?.find((f) => String(f.enc_id) === String(segments[0]?.floorId));
        if (fromFloor) dispatch(setCurrentFloor(fromFloor));
        setSegmentIndex(0);
        fitSegmentOnMap(segments[0]?.positions);
        return true;
    }, [paths, floorList, allPins, dispatch, fitSegmentOnMap, buildVtMovementMap, buildFloorOrderMap]);


    // const runPathAndDispatch = useCallback((fromPinId, toPinId, allowedVtPinIds = null) => {
    //     const startId = findPinNodeId(paths, fromPinId);
    //     const endId   = findPinNodeId(paths, toPinId);

    //     if (!startId) {
    //         setPathError('The "From" pin is not connected to any navigation path. Draw a connection first.');
    //         return false;
    //     }
    //     if (!endId) {
    //         setPathError('The "To" pin is not connected to any navigation path. Draw a connection first.');
    //         return false;
    //     }

    //     const vtMovementMap = buildVtMovementMap();
    //     const floorOrderMap = buildFloorOrderMap();
 
    //     let result = null;
    //     let nodes  = null;

    //     if (allowedVtPinIds !== null && allowedVtPinIds.size > 0) { 
    //         const sortedVtPinIds = findSortedVtPinIds(paths, startId, allowedVtPinIds);
 
    //         for (const vtPinId of sortedVtPinIds) {
    //             const singleVtSet          = new Set([vtPinId]);
    //             const { nodes: n, adj: a } = buildNavGraph(
    //                 paths, true, singleVtSet, vtMovementMap, floorOrderMap,
    //             );
    //             const candidate = dijkstra(n, a, startId, endId);
    //             if (candidate) {
    //                 result = candidate;
    //                 nodes  = n;
    //                 break;
    //             }
    //         }
 
    //         if (!result) {
    //             const { nodes: n, adj: a } = buildNavGraph(
    //                 paths, true, allowedVtPinIds, vtMovementMap, floorOrderMap,
    //             );
    //             result = dijkstra(n, a, startId, endId);
    //             nodes  = n;
    //         }
    //     } else { 
    //         const { nodes: n, adj: a } = buildNavGraph(
    //             paths, true, null, vtMovementMap, floorOrderMap,
    //         );
    //         result = dijkstra(n, a, startId, endId);
    //         nodes  = n;
    //     } 
    //     if (!result) {
    //         setPathError(
    //             allowedVtPinIds
    //                 ? 'No path found via the selected transport type. Try a different option.'
    //                 : 'No connected path found between the selected locations.'
    //         );
    //         return false;
    //     }

    //     const nodeFloorMap   = {};
    //     const nodeIdToPinId  = {};
    //     for (const path of paths) {
    //         for (const pt of path.points) {
    //             nodeFloorMap[pt.id]  = path.floorId;
    //             if (pt.pinId) nodeIdToPinId[pt.id] = String(pt.pinId);
    //         }
    //     }

    //     const vtEncIds = new Set((allPins?.vertical || []).map((vt) => String(vt.enc_id)));

    //     const positions = result.nodeIds.map((id) => nodes[id]).filter(Boolean);

    //     let realDistanceM = 0;
    //     for (let i = 1; i < positions.length; i++) {
    //         realDistanceM += haversineM(positions[i - 1], positions[i]);
    //     }

    //     const rawSegments = [];
    //     for (const nodeId of result.nodeIds) {
    //         const pos     = nodes[nodeId];
    //         const floorId = nodeFloorMap[nodeId];
    //         if (!pos) continue;
    //         const lastSeg = rawSegments[rawSegments.length - 1];
    //         if (!lastSeg || lastSeg.floorId !== floorId) {
    //             const floor     = floorList?.find((f) => String(f.enc_id) === String(floorId));
    //             const floorName = floor?.fp_name || floor?.floor_plan || `Floor ${floorId}`;
    //             rawSegments.push({ floorId, floorName, positions: [pos], _nodeIds: [nodeId] });
    //         } else {
    //             lastSeg.positions.push(pos);
    //             lastSeg._nodeIds.push(nodeId);
    //         }
    //     }

    //     const segments = rawSegments
    //         .filter((seg, i) => {
    //             const isIntermediate = i > 0 && i < rawSegments.length - 1;
    //             if (!isIntermediate) return true;
    //             if (seg._nodeIds.length !== 1) return true;
    //             const pinId = nodeIdToPinId[seg._nodeIds[0]];
    //             return !(pinId && vtEncIds.has(pinId));
    //         })
    //         .map(({ _nodeIds, ...seg }) => seg);

    //     dispatch(setShortestPath({
    //         positions,
    //         distanceM:    realDistanceM,
    //         segments,
    //         isMultiFloor: segments.length > 1,
    //     }));

    //     const fromFloor = floorList?.find((f) => String(f.enc_id) === String(segments[0]?.floorId));
    //     if (fromFloor) dispatch(setCurrentFloor(fromFloor));
    //     setSegmentIndex(0);
    //     fitSegmentOnMap(segments[0]?.positions);
    //     return true;
    // }, [paths, floorList, allPins, dispatch, fitSegmentOnMap, buildVtMovementMap, buildFloorOrderMap]);


    const computeVtTypes = useCallback((fromFloorId, toFloorId, wheelchair) => {
        const vtList      = allPins?.vertical || [];
        const goingUp     = isGoingUp(fromFloorId, toFloorId);

        const vtOnFrom = new Set(
            paths
                .filter((p) => String(p.floorId) === String(fromFloorId))
                .flatMap((p) => p.points.filter((pt) => pt.pinId).map((pt) => String(pt.pinId)))
        );
        const vtOnTo = new Set(
            paths
                .filter((p) => String(p.floorId) === String(toFloorId))
                .flatMap((p) => p.points.filter((pt) => pt.pinId).map((pt) => String(pt.pinId)))
        );

        const typeMap = {}; 
        for (const vt of vtList) {
            const typeKey = String(vt.icon_id ?? vt.icon ?? vt.vt_name ?? vt.enc_id);
            let name = vt.icon == '<GrEscalator/>' ? 'Escalator' : vt.icon == '<GiStairs/>' ? "Stair" : vt.icon == '<GrElevator/>' ? "Elevator" :"Ramp" 

            if (!typeMap[typeKey]) {
                typeMap[typeKey] = {
                    icon_id:       typeKey,
                    name:          name ,//vt.vt_name || vt.title || 'Transport',
                    path:          vt.path || vt.icon_path || '',
                    onFrom:        false,  
                    onTo:          false,  
                    hasWheelchair: false,
                    vtIds:         [],
                };
            }

            const encId = String(vt.enc_id);
            const dir   = vt.movement_direction || 'bidirectional';

            const dirOk = dir === 'bidirectional' 
                       || (goingUp  && dir === 'up')
                       || (!goingUp && dir === 'down');

            typeMap[typeKey].vtIds.push(encId);

           
            if (vtOnFrom.has(encId) && dirOk) typeMap[typeKey].onFrom = true;

            if (vtOnTo.has(encId)) typeMap[typeKey].onTo = true;

            if (vt.is_wheelchair === 1) typeMap[typeKey].hasWheelchair = true;
        }
        return Object.values(typeMap).map((t) => ({
            ...t,
            
            available: t.onFrom && t.onTo && (!wheelchair || t.hasWheelchair),
        }));
    }, [allPins, paths, isGoingUp]);
 

    const findPath = () => {
        const fromPinId   = currentFormDetails?.from_pin_id;
        const toPinId     = currentFormDetails?.to_pin_id;
        const fromFloorId = currentFormDetails?.from_floor_id;
        const toFloorId   = currentFormDetails?.to_floor_id;

        setPathError(null);
        dispatch(clearShortestPath());
        setSegmentIndex(null);

        if (!fromPinId || !toPinId) {
            setPathError('Please select both From and To locations.');
            return;
        }
        if (fromPinId === toPinId) {
            setPathError('From and To locations must be different.');
            return;
        }
 
        if (fromFloorId && toFloorId && String(fromFloorId) !== String(toFloorId)) {
            const types = computeVtTypes(fromFloorId, toFloorId, isWheelchair); 
            setVtTypes(types);
            setShowVtModal(true);
            return;
        } 
 
        runPathAndDispatch(fromPinId, toPinId, null);
    };
 

    const handleVtSelect = (selectedIconId) => {
        setShowVtModal(false);

        const fromPinId = currentFormDetails?.from_pin_id;
        const toPinId   = currentFormDetails?.to_pin_id;
        const vtList    = allPins?.vertical || [];

        const allowedVtPinIds = new Set(
            vtList
                .filter((vt) => {
                    const typeKey = String(vt.icon_id ?? vt.icon ?? vt.vt_name ?? vt.enc_id);
                    return typeKey === String(selectedIconId) &&
                           (!isWheelchair || vt.is_wheelchair === 1);
                })
                .map((vt) => String(vt.enc_id))
        );
 

        runPathAndDispatch(fromPinId, toPinId, allowedVtPinIds);
    };

    const handleVtClose = () => {
        setShowVtModal(false);
    };

    const handleWheelchairChange = (checked) => {
        setIsWheelchair(checked); 
        const fromFloorId = currentFormDetails?.from_floor_id;
        const toFloorId   = currentFormDetails?.to_floor_id;
        if (fromFloorId && toFloorId) {
            setVtTypes(computeVtTypes(fromFloorId, toFloorId, checked));
        }
    };
 

    const handleStep = () => {
        const newIndex = segmentIndex + 1;
        const seg   = shortestPath?.segments?.[newIndex];
        const floor = floorList?.find((f) => String(f.enc_id) === String(seg?.floorId));
        const floorChanging = floor && String(floor.enc_id) !== String(currentFloor?.enc_id);
        if (floor) dispatch(setCurrentFloor(floor));
        setSegmentIndex(newIndex);
        if (floorChanging) {
            pendingFitRef.current = seg?.positions;
        } else {
            fitSegmentOnMap(seg?.positions);
        }
        fitSegmentOnMap(seg?.positions);
    };

    const handlePrevious = () => {
        const newIndex = segmentIndex - 1;
        const seg   = shortestPath?.segments?.[newIndex];
        const floor = floorList?.find((f) => String(f.enc_id) === String(seg?.floorId));
        const floorChanging = floor && String(floor.enc_id) !== String(currentFloor?.enc_id);
        if (floor) dispatch(setCurrentFloor(floor));
        setSegmentIndex(newIndex);
        if (floorChanging) {
            pendingFitRef.current = seg?.positions;
        } else {
            fitSegmentOnMap(seg?.positions);
        }
        fitSegmentOnMap(seg?.positions);
    };

    const handleEnd = () => {
        dispatch(clearShortestPath());
        setPathError(null);
        setSegmentIndex(null);
        setCurrentFormDetails(null);
    };

    const clearPath = () => {
        dispatch(clearShortestPath());
        setPathError(null);
        setSegmentIndex(null);
    };

    const destinationFloorName = (() => {
        const toFloorId = currentFormDetails?.to_floor_id;
        if (!toFloorId) return '';
        const floor = floorList?.find((f) => String(f.enc_id) === String(toFloorId)); 
        return floor?.floor_plan || floor?.floor_name || floor?.name || `Floor ${toFloorId}`;
    })();

    return (
        <div className="bar" id="inner-customizer2" style={{ position: 'relative', height: mapDivSize, paddingBottom: '20px' }}>
            <Row className="backRow">
                <Col md={8}>
                    <h1>Navigation Path</h1>
                </Col>
                <Col md={4}>
                    <div className="backArrow float-right" onClick={goBack}>
                        <BsArrowLeftShort />
                    </div>
                </Col>
            </Row>

            <Formik
                initialValues={{ from: '', to: '' }}
                onSubmit={() => {}}
                enableReinitialize
            >
                {({ errors, touched, handleSubmit, setFieldValue }) => (
                    <form onSubmit={handleSubmit}>
                        <div className="custom-scrollbar customScroll" style={{ height: window.innerHeight - 80 }}> 
                            <div className="bar-sub">
                                <NavigationFormFields
                                    errors={errors}
                                    touched={touched}
                                    setFieldValue={setFieldValue}
                                    options={options}
                                    findPath={findPath}
                                    onClear={clearPath}
                                    pathResult={shortestPath}
                                    pathError={pathError}
                                    selTraversibleDetails={currentFormDetails}
                                    setSelTraversibleDetails={setCurrentFormDetails}
                                    segmentIndex={segmentIndex}
                                    onStep={handleStep}
                                    onPrevious={handlePrevious}
                                    onEnd={handleEnd}
                                />
                            </div>
                        </div>
                    </form>
                )}
            </Formik>
 
            <VtSelectionModal
                isOpen={showVtModal}
                onClose={handleVtClose}
                destinationFloorName={destinationFloorName}
                vtTypes={vtTypes}
                isWheelchair={isWheelchair}
                onWheelchairChange={handleWheelchairChange}
                onSelect={handleVtSelect}
            />
        </div>
    );
}

export default Navigation;
